import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "error",
    "express",
    "expressSection",
    "headline",
    "originalPlanAmount",
    "originalRenewalAmount",
    "originalTodayAmount",
    "offerPlanName",
    "offerTerms",
    "payment",
    "planAmount",
    "planRenewalLabel",
    "promotionApplied",
    "promotionAppliedCode",
    "promotionAppliedLabel",
    "promotionApply",
    "promotionCode",
    "promotionDescription",
    "promotionEntry",
    "promotionError",
    "renewalDisclosure",
    "renewalAmount",
    "skeleton",
    "submit",
    "todayAmount",
    "trialDescription"
  ]

  static values = {
    context: String,
    offerDiscount: Boolean,
    paidTrial: Boolean,
    promotionUrl: String,
    publishableKey: String,
    sessionUrl: String,
    telemetryUrl: String,
    trialDays: Number
  }

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.initializeCheckout()
  }

  disconnect() {
    this.paymentElement?.unmount()
    this.expressElement?.unmount()
  }

  async initializeCheckout() {
    try {
      const response = await fetch(this.sessionUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ context: this.contextValue })
      })
      const payload = await response.json().catch(() => ({}))

      if (!response.ok) throw new Error(payload.error || "Secure checkout couldn't be started.")
      if (typeof window.Stripe !== "function") throw new Error("Secure checkout couldn't be loaded.")

      const stripe = window.Stripe(this.publishableKeyValue)
      const checkoutOptions = {
        clientSecret: payload.client_secret,
        adaptivePricing: {
          allowed: true
        },
        elementsOptions: {
          savedPaymentMethod: {
            enableSave: "never",
            enableRedisplay: "never"
          },
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#312e81",
              colorText: "#1c1917",
              colorDanger: "#b91c1c",
              colorBackground: "#ffffff",
              fontFamily: "Inter, system-ui, sans-serif",
              borderRadius: "12px",
              spacingUnit: "4px"
            },
            rules: {
              ".Input": { border: "1px solid #d6d3d1", boxShadow: "none", padding: "14px" },
              ".Input:focus": { border: "1px solid #4f46e5", boxShadow: "0 0 0 1px #4f46e5" },
              ".Label": { color: "#57534e", fontWeight: "500" }
            }
          }
        }
      }

      this.checkout = typeof stripe.initCheckoutElementsSdk === "function" ?
        stripe.initCheckoutElementsSdk(checkoutOptions) :
        stripe.initCheckout(checkoutOptions)

      this.mountPaymentElement()
      this.mountExpressCheckoutElement()

      const loadResult = await this.checkout.loadActions()
      if (loadResult.type !== "success") throw new Error(loadResult.error?.message || "Secure checkout couldn't be loaded.")

      this.actions = loadResult.actions
      const session = this.actions.getSession()
      this.updateSummary(session)
      // Seed the button from the loaded session rather than waiting for the first
      // change event, so a session that is already confirmable is not left behind a
      // disabled button.
      this.submitTarget.disabled = !session.canConfirm
      this.restoreAppliedPromotion()
      this.checkout.on("change", (session) => {
        this.updateSummary(session)
        this.submitTarget.disabled = !session.canConfirm
      })
    } catch (error) {
      this.showError(error.message)
    }
  }

  mountPaymentElement() {
    this.skeletonTarget.remove()
    this.paymentElement = this.checkout.createPaymentElement({ layout: "tabs" })
    this.paymentElement.mount(this.paymentTarget)
  }

  mountExpressCheckoutElement() {
    if (typeof this.checkout.createExpressCheckoutElement !== "function") return

    this.expressElement = this.checkout.createExpressCheckoutElement({
      buttonHeight: 48,
      buttonType: { applePay: "plain", googlePay: "plain" },
      layout: { maxColumns: 2, maxRows: 1, overflow: "auto" }
    })
    this.expressElement.on("ready", ({ availablePaymentMethods }) => {
      this.expressSectionTarget.classList.toggle("hidden", !availablePaymentMethods)
      // When no wallets are available this row hides itself and leaves no trace, so
      // record what the buyer was actually offered.
      this.track("elements_wallets_resolved", {
        wallets: this.offeredWallets(availablePaymentMethods),
        wallets_available: Boolean(availablePaymentMethods)
      })
    })
    this.expressElement.on("confirm", (event) => this.confirmExpressCheckout(event))
    this.expressElement.mount(this.expressTarget)
  }

  async submit(event) {
    event.preventDefault()
    if (this.confirming || !this.actions) return

    this.confirming = true
    this.submitTarget.disabled = true
    this.hideError()

    try {
      const result = await this.actions.confirm()
      if (result.type === "error") throw this.confirmFailure(result.error, "payment_element")
    } catch (error) {
      this.confirming = false
      this.submitTarget.dispatchEvent(new CustomEvent("loading-button:stop"))
      this.submitTarget.disabled = !this.actions.getSession().canConfirm
      this.showError(error.message)
    }
  }

  async confirmExpressCheckout(event) {
    if (this.confirming || !this.actions) return

    this.confirming = true
    this.hideError()

    try {
      const result = await this.actions.confirm({ expressCheckoutConfirmEvent: event })
      if (result.type === "error") throw this.confirmFailure(result.error, "express_checkout")
    } catch (error) {
      this.confirming = false
      this.showError(error.message)
    }
  }

  async applyPromotionCode(event) {
    event?.preventDefault()
    if (!this.actions || !this.hasPromotionCodeTarget) return

    const code = this.promotionCodeTarget.value.trim()
    if (!code) return

    this.promotionErrorTarget.classList.add("hidden")
    this.promotionApplyTarget.disabled = true

    let result
    try {
      result = await this.actions.applyPromotionCode(code)
    } catch (error) {
      result = { error }
    }

    if (result.type === "error" || result.error) {
      this.promotionErrorTarget.textContent = result.error?.message || "That coupon code isn't valid."
      this.promotionErrorTarget.classList.remove("hidden")
      this.promotionApplyTarget.disabled = false
    } else {
      this.promotionCodeTarget.value = ""
      this.appliedPromotion = { code }
      this.storeAppliedPromotion(code)
      this.showAppliedPromotion()
      this.updateSummary(this.actions.getSession())

      this.appliedPromotion = { code, ...await this.fetchPromotionDetails(code) }
      this.showAppliedPromotion()
      this.updateSummary(this.actions.getSession())
    }
  }

  async removePromotionCode() {
    if (!this.actions || !this.appliedPromotion) return

    const result = await this.actions.removePromotionCode()
    if (result?.type === "error" || result?.error) {
      this.showError(result.error?.message || "The coupon couldn't be removed.")
      return
    }

    this.appliedPromotion = null
    this.clearStoredPromotion()
    this.promotionAppliedTarget.classList.add("hidden")
    this.promotionEntryTarget.classList.remove("hidden")
    this.promotionEntryTarget.open = true
    this.promotionApplyTarget.disabled = false
    this.updateSummary(this.actions.getSession())
  }

  async fetchPromotionDetails(code) {
    try {
      const url = new URL(this.promotionUrlValue, window.location.origin)
      url.searchParams.set("context", this.contextValue)
      url.searchParams.set("code", code)

      const response = await fetch(url, { headers: { "Accept": "application/json" } })
      return response.ok ? await response.json() : {}
    } catch {
      return {}
    }
  }

  async restoreAppliedPromotion() {
    const code = this.storedPromotionCode()
    if (!code || !this.hasPromotionEntryTarget) return

    this.appliedPromotion = { code }
    this.showAppliedPromotion()
    this.updateSummary(this.actions.getSession())

    this.appliedPromotion = { code, ...await this.fetchPromotionDetails(code) }
    this.showAppliedPromotion()
    this.updateSummary(this.actions.getSession())
  }

  storeAppliedPromotion(code) {
    try {
      sessionStorage.setItem(this.promotionStorageKey, code)
    } catch {
      // Checkout still works when storage is unavailable.
    }
  }

  storedPromotionCode() {
    try {
      return sessionStorage.getItem(this.promotionStorageKey)
    } catch {
      return null
    }
  }

  clearStoredPromotion() {
    try {
      sessionStorage.removeItem(this.promotionStorageKey)
    } catch {
      // Checkout still works when storage is unavailable.
    }
  }

  get promotionStorageKey() {
    return `premium-checkout-promotion:${this.contextValue}`
  }

  showAppliedPromotion() {
    const { code, percent_off: percentOff } = this.appliedPromotion
    const discountLabel = percentOff ? `${this.formatNumber(percentOff)}% off` : "Discount applied"

    this.promotionAppliedCodeTarget.textContent = code.toUpperCase()
    this.promotionAppliedLabelTarget.textContent = `· ${discountLabel}`
    this.promotionEntryTarget.classList.add("hidden")
    this.promotionAppliedTarget.classList.remove("hidden")
  }

  updateSummary(session) {
    this.currentSession = session
    const recurringItem = session.lineItems?.find((item) => item.recurring) || session.lineItems?.[0]
    const interval = recurringItem?.recurring?.interval
    const planAmount = recurringItem?.subtotal?.amount
    const totalToday = session.total?.total?.amount
    const renewalAmount = session.recurring?.dueNext?.total?.amount || planAmount
    const intervalLabel = {
      day: "/day",
      week: "/week",
      month: "/month",
      year: "/year"
    }[interval] || ""
    const firstBillingPeriod = {
      day: "first day",
      week: "first week",
      month: "first month",
      year: "first year"
    }[interval]
    const planName = {
      day: "Daily plan",
      week: "Weekly plan",
      month: "Monthly plan",
      year: "Annual plan"
    }[interval]
    const cadence = {
      day: "a day",
      week: "a week",
      month: "a month",
      year: "a year"
    }[interval]
    const renewalCadence = {
      day: "daily",
      week: "weekly",
      month: "monthly",
      year: "every 12 months"
    }[interval]
    const promotionAppliesToFuturePayments = ["forever", "repeating"].includes(this.appliedPromotion?.duration)
    const discountedRecurringAmount = promotionAppliesToFuturePayments && !this.paidTrialValue && this.trialDaysValue === 0 && totalToday !== planAmount ?
      totalToday : renewalAmount
    const discountAppliesToday = (this.offerDiscountValue || this.appliedPromotion) &&
      !this.paidTrialValue && this.trialDaysValue === 0 && totalToday !== planAmount

    this.updateOriginalAmount(this.originalTodayAmountTargets, null)
    this.updateOriginalAmount(this.originalPlanAmountTargets, null)
    this.updateOriginalAmount(this.originalRenewalAmountTargets, null)

    if (discountAppliesToday) {
      this.updateOriginalAmount(this.originalTodayAmountTargets, planAmount)
    }

    this.planAmountTargets.forEach((target) => {
      const amount = promotionAppliesToFuturePayments ? discountedRecurringAmount : planAmount
      target.textContent = amount ? `${amount}${intervalLabel}` : "—"
      if (this.appliedPromotion && amount !== planAmount) {
        this.updateOriginalAmount(this.originalPlanAmountTargets, `${planAmount}${intervalLabel}`)
      }
    })
    if (this.hasPlanRenewalLabelTarget && firstBillingPeriod) {
      this.planRenewalLabelTarget.textContent = `Renews after ${firstBillingPeriod}`
    }
    if (this.hasOfferPlanNameTarget && planName) {
      this.offerPlanNameTarget.textContent = planName
    }
    if (this.hasOfferTermsTarget && totalToday && renewalAmount && firstBillingPeriod && renewalCadence) {
      const offerRenewalCadence = interval === "year" ? "annually" : renewalCadence
      this.offerTermsTarget.textContent = `${totalToday} for the ${firstBillingPeriod}, then renews ${offerRenewalCadence} at ${renewalAmount}. Cancel anytime.`
    }
    this.todayAmountTargets.forEach((target) => {
      target.textContent = totalToday || "—"
      target.classList.toggle("text-green-700", Boolean(discountAppliesToday))
    })
    if (this.paidTrialValue && totalToday) {
      this.headlineTarget.textContent = `Get ${this.trialDaysValue} days of Premium for ${totalToday}`
    }
    this.renewalAmountTargets.forEach((target) => {
      target.textContent = renewalAmount ? `${renewalAmount}${intervalLabel}` : "—"
      if (this.appliedPromotion && renewalAmount !== planAmount) {
        this.updateOriginalAmount(this.originalRenewalAmountTargets, `${planAmount}${intervalLabel}`)
      }
    })
    if (this.hasTrialDescriptionTarget && renewalAmount && cadence) {
      this.trialDescriptionTarget.textContent = `Then just ${renewalAmount} ${cadence} after your trial. No commitment, cancel anytime.`
    }
    if (this.hasRenewalDisclosureTarget && renewalAmount && renewalCadence) {
      this.renewalDisclosureTarget.textContent = `You’ll be charged ${renewalAmount} when your ${this.trialDaysValue}-day trial ends. Your subscription renews ${renewalCadence} until canceled.`
    }
    if (this.appliedPromotion) {
      this.promotionDescriptionTarget.textContent = this.promotionDescription(planAmount, discountedRecurringAmount, intervalLabel)
    }

    this.submitTarget.disabled = !session.canConfirm
  }

  promotionDescription(regularAmount, discountedAmount, intervalLabel) {
    const { duration, duration_in_months: durationInMonths, percent_off: percentOff } = this.appliedPromotion
    const discountLabel = percentOff ? `${this.formatNumber(percentOff)}% off` : "The discount"

    if (duration === "forever") {
      return `${discountLabel} applies to every subscription payment. You'll pay ${discountedAmount}${intervalLabel} while your subscription remains active.`
    }
    if (duration === "once") {
      return `${discountLabel} applies to this payment only. Future payments are ${regularAmount}${intervalLabel}.`
    }
    if (duration === "repeating" && durationInMonths) {
      return `${discountLabel} applies to subscription payments for ${durationInMonths} months. After that, payments are ${regularAmount}${intervalLabel}.`
    }

    return "Your discounted total is reflected above. Future payments follow the coupon's terms."
  }

  updateOriginalAmount(targets, amount) {
    targets.forEach((target) => {
      target.textContent = amount || ""
      target.classList.toggle("hidden", !amount)
    })
  }

  formatNumber(value) {
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
  }

  showError(message) {
    this.errorTarget.textContent = message || "Something went wrong. Please try again."
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  offeredWallets(availablePaymentMethods) {
    const offered = Object.entries(availablePaymentMethods || {})
      .filter(([ , available ]) => available)
      .map(([ wallet ]) => wallet)
      .sort()

    return offered.length ? offered.join(",") : "none"
  }

  // Records why a confirm attempt failed and returns the Error to raise, so the
  // caller keeps its existing throw/catch shape. Only Stripe's structured codes are
  // sent: the message is free text that can carry issuer wording.
  confirmFailure(error, surface) {
    this.track("elements_confirm_failed", {
      surface: surface,
      error_type: error?.type,
      error_code: error?.code,
      decline_code: error?.decline_code
    })

    return new Error(error?.message)
  }

  track(name, properties) {
    if (!this.hasTelemetryUrlValue) return

    // keepalive so a beacon still lands when Stripe navigates away mid-flight, and
    // failures stay silent: telemetry must never interrupt someone paying.
    fetch(this.telemetryUrlValue, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({ context: this.contextValue, name: name, properties: properties })
    }).catch(() => {})
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
